'use client';

import React from 'react';
import { isInstagramOrFacebookInAppBrowser, getBrowserInfo } from '@/lib/browserDetection';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  const browserInfo = getBrowserInfo();
  const isInAppBrowser = isInstagramOrFacebookInAppBrowser();

  // Log error for debugging
  React.useEffect(() => {
    console.error('Global application error:', error);
    console.log('Browser info:', browserInfo);
  }, [error, browserInfo]);

  return (
    <html>
      <body>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Application Error
              </h1>
              <p className="text-gray-600 mb-4">
                A critical error occurred. Please try refreshing the page or contact support.
              </p>
            </div>

            {isInAppBrowser && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Recommended Action
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        You&apos;re using an in-app browser which may cause compatibility issues.
                        For the best experience, please open this link in your device&apos;s default browser.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={reset}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try again
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh page
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <details className="mt-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer">
                  Error details (development only)
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-700 overflow-auto">
                  <div><strong>Error:</strong> {error.message}</div>
                  {error.digest && <div><strong>Digest:</strong> {error.digest}</div>}
                  <div><strong>Browser:</strong> {browserInfo.browserName}</div>
                  <div><strong>Platform:</strong> {browserInfo.platform}</div>
                  <div><strong>In-app browser:</strong> {browserInfo.isInAppBrowser ? 'Yes' : 'No'}</div>
                  <div><strong>Secure context:</strong> {browserInfo.isSecureContext ? 'Yes' : 'No'}</div>
                  <div><strong>Has storage:</strong> {browserInfo.hasStorage ? 'Yes' : 'No'}</div>
                  <div><strong>Stack trace:</strong></div>
                  <pre className="mt-1 text-xs overflow-auto">{error.stack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
